import {BaseDashlet} from "../../lq_js_common/dashboard/base_dashlet";
import {DashboardGraph} from "../../graphs/dashboard_graph";
import {GraphOptionsBuilder} from "../../lq_js_common/e_charts/chart_builder";
import {MinMaxSeries} from "../../lq_js_common/e_charts/min_max_median_series";
import {
    attachDirectionHint,
    applySplitDirectionAxes,
    createBoundedAxisTooltip,
    finalizeSplitDirectionChart,
    formatBitsPerSecondAxis,
    formatTimeTick,
} from "../../helpers/chart_common";
import {formatTimeRange} from "../../helpers/tooltips";
import {attachShowLimitsToggle, showLimitsEnabled} from "../../helpers/show_limits";
import {debugLog} from '../../helpers/debug_log';

const SHOW_LIMITS_STORAGE_KEY = "lqos_shaper_throughput_show_limits";

export class ShaperThroughput extends BaseDashlet {
    constructor(slot) {
        super(slot);
        this.rendered = false;
        this.lastGraphState = null;
        this.explainIssues = null;
        this.showLimits = showLimitsEnabled(SHOW_LIMITS_STORAGE_KEY);
    }

    title() {
        return "Shaper Throughput";
    }

    supportsZoom() {
        return true;
    }

    tooltip() {
        return "<h5>Shaper Throughput</h5><p>Bandwidth use (speed) during the specified time period.</p>";
    }

    canExplain() {
        return true;
    }

    isExplainReady() {
        return this.explainIssues !== null;
    }

    explain() {
        if (!this.explainIssues || this.explainIssues.length === 0) return null;
        return { region: "Shaper Throughput", issues: this.explainIssues };
    }

    buildContainer() {
        let base = super.buildContainer();
        attachDirectionHint(base);
        attachShowLimitsToggle(base, this.id, SHOW_LIMITS_STORAGE_KEY, (checked) => {
            this.showLimits = checked;
            if (this.graph && this.lastGraphState !== null) {
                this.graph.update(this.lastGraphState, checked);
            }
        });
        base.appendChild(this.graphDiv());
        return base;
    }

    setup() {
        super.setup();
        this.graph = new ShaperThroughputGraph(this.graphDivId());
        if (this.lastGraphState !== null) {
            this.graph.update(this.lastGraphState, this.showLimits);
        }
    }

    channelRequests() {
        return [ "ShaperDetail" ];
    }

    onMessage(msg) {
        if (msg.Data.target !== "shaper_detail") return;

        if (!this.rendered) {
            let myDiv = document.getElementById(this.id);
            myDiv.classList.add("card", "animate__animated", "animate__fadeIn");
        } else {
            let myDiv = document.getElementById(this.id);
            myDiv.classList.add("animate__animated", "animate__pulse");
            setTimeout(() => {
                myDiv.classList.remove("animate__animated", "animate__pulse");
            }, 1000);
        }
        for (const [key, value] of Object.entries(msg.Data.blob)) {
            this.graph.update(value, this.showLimits);
            this.lastGraphState = value;
            this.explainIssues = buildThroughputIssues(value);
        }
    }

}

function findFirstLastFiniteIndex(values, limit) {
    let first = -1;
    let last = -1;
    const len = Number.isFinite(limit) ? Math.max(0, Math.min(values.length, limit)) : values.length;
    for (let i = 0; i < len; i += 1) {
        if (Number.isFinite(values[i])) {
            first = i;
            break;
        }
    }
    for (let i = len - 1; i >= 0; i -= 1) {
        if (Number.isFinite(values[i])) {
            last = i;
            break;
        }
    }
    return { first, last };
}

function buildSaturationIssue(label, series, capacityBps, ticks) {
    if (!Number.isFinite(capacityBps) || capacityBps <= 0) return null;
    const samples = Array.isArray(series) ? series : [];
    const tickSeries = Array.isArray(ticks) ? ticks : [];
    const len = Math.min(samples.length, tickSeries.length);
    if (len < 2) return null;

    let total = 0;
    let approach = 0;
    for (let i = 0; i < len; i += 1) {
        const v = samples[i];
        if (!Number.isFinite(v)) continue;
        total += 1;
        const ratio = (v * 8) / capacityBps;
        if (ratio >= 0.9) {
            approach += 1;
        }
    }
    if (total === 0 || approach === 0) return null;

    const { first, last } = findFirstLastFiniteIndex(tickSeries, len);
    const timeRange = (first >= 0 && last >= 0)
        ? formatTimeRange(tickSeries[first], tickSeries[last])
        : null;
    const pct = Math.round((approach / total) * 100);
    return {
        time_range: timeRange,
        severity: "orange",
        issue: `${label} near capacity`,
        details: `At/near capacity in ${pct}% of samples`,
    };
}

function buildThroughputIssues(data) {
    if (!data?.body || !data?.header) return [];
    const issues = [];
    const ticks = data.body.ticks || [];
    const downSeries = data.body.bytes_per_second_down_median || [];
    const upSeries = data.body.bytes_per_second_up_median || [];
    const downCapBps = Number(data.header.max_mbps_down || 0) * 1_000_000;
    const upCapBps = Number(data.header.max_mbps_up || 0) * 1_000_000;

    const downIssue = buildSaturationIssue("Download", downSeries, downCapBps, ticks);
    if (downIssue) issues.push(downIssue);
    const upIssue = buildSaturationIssue("Upload", upSeries, upCapBps, ticks);
    if (upIssue) issues.push(upIssue);
    return issues;
}

class ShaperThroughputGraph extends DashboardGraph {
    constructor(id) {
        super(id);
        this.option = new GraphOptionsBuilder()
            .withLeftGridSize(75)
            .withTimeAxis()
            .withScaledAbsYAxis("Bandwidth", 50)
            .withEmptyLegend()
            .withEmptySeries()
            .build();
        this.option && this.chart.setOption(this.option);
    }

    update(data, showLimits = true) {
        this.chart.hideLoading();

        this.option.series = [];
        this.option.legend = { show: false, data: [] };

        let down = new MinMaxSeries("Download", 0);
        let up = new MinMaxSeries("Upload", 0);
        applySplitDirectionAxes(this.option, {
            ticks: data.body.ticks || [],
            topLabel: "Download",
            bottomLabel: "Upload",
            yFormatter: formatBitsPerSecondAxis,
        });
        down.series.forEach((series) => {
            series.xAxisIndex = 0;
            series.yAxisIndex = 0;
            series.showSymbol = false;
        });
        up.series.forEach((series) => {
            series.xAxisIndex = 1;
            series.yAxisIndex = 0;
            series.showSymbol = false;
            series.z = 4;
        });

        const downLimitBps = Number(data?.header?.max_mbps_down || 0) * 1_000_000;
        const upLimitBps = Number(data?.header?.max_mbps_up || 0) * 1_000_000;
        if (showLimits && downLimitBps && downLimitBps > 0) {
            down.series[0].markLine = {
                silent: true,
                symbol: 'none',
                label: { formatter: `Max Download (${formatBitsPerSecondAxis(downLimitBps)})`, position: 'insideEndTop', color: '#ff9d9d', textBorderColor: 'rgba(30,30,30,0.92)' },
                lineStyle: { type: 'dashed', opacity: 0.7, color: '#ff0000' },
                data: [{ yAxis: downLimitBps }],
            };
        }
        if (showLimits && upLimitBps && upLimitBps > 0) {
            up.series[0].markLine = {
                silent: true,
                symbol: 'none',
                label: { formatter: `Max Upload (${formatBitsPerSecondAxis(upLimitBps)})`, position: 'insideEndBottom', color: '#ff9d9d', textBorderColor: 'rgba(30,30,30,0.92)' },
                lineStyle: { type: 'dashed', opacity: 0.7, color: '#ff0000' },
                data: [{ yAxis: 0 - upLimitBps }],
            };
        }

        for (let x=0; x<data.body.active_circuits.length; x++) {
            down.pushPositive(
                data.body.bytes_per_second_down_median[x] * 8,
                data.body.bytes_per_second_down_min[x] * 8,
                data.body.bytes_per_second_down_max[x] * 8,
            );
            up.pushNegative(
                data.body.bytes_per_second_up_median[x] * 8,
                data.body.bytes_per_second_up_min[x] * 8,
                data.body.bytes_per_second_up_max[x] * 8,
            );
        }
        down.addToOptions(this.option);
        up.addToOptions(this.option);
        this.option.tooltip = createBoundedAxisTooltip({
            linkAllXAxes: true,
            yFormatter: formatBitsPerSecondAxis,
            formatter: (params) => {
                if (!params || params.length === 0) return "";
                const timeIndex = params[0].dataIndex;
                const downMin = data.body.bytes_per_second_down_min[timeIndex] * 8;
                const downMedian = data.body.bytes_per_second_down_median[timeIndex] * 8;
                const downMax = data.body.bytes_per_second_down_max[timeIndex] * 8;
                const upMin = data.body.bytes_per_second_up_min[timeIndex] * 8;
                const upMedian = data.body.bytes_per_second_up_median[timeIndex] * 8;
                const upMax = data.body.bytes_per_second_up_max[timeIndex] * 8;
                return `<div style="font-size: 10px;">
${formatTimeTick(params[0].axisValue)}<br/>
<strong>Download</strong><br/>
&nbsp;&nbsp;Min: ${formatBitsPerSecondAxis(downMin)}<br/>
&nbsp;&nbsp;Median: ${formatBitsPerSecondAxis(downMedian)}<br/>
&nbsp;&nbsp;Max: ${formatBitsPerSecondAxis(downMax)}<br/>
<strong>Upload</strong><br/>
&nbsp;&nbsp;Min: ${formatBitsPerSecondAxis(upMin)}<br/>
&nbsp;&nbsp;Median: ${formatBitsPerSecondAxis(upMedian)}<br/>
&nbsp;&nbsp;Max: ${formatBitsPerSecondAxis(upMax)}
</div>`;
            },
        });

        // Add axis pointer configuration for highlighting
        this.option.axisPointer = {
            link: {xAxisIndex: 'all'},
            label: {
                backgroundColor: 'rgba(30,30,30,0.92)',
                color: '#fff',
                fontFamily: 'inherit'
            }
        };
        
        debugLog(this.option);
        finalizeSplitDirectionChart(this.option);
        this.chart.setOption(this.option, true);
    }
}
